import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { fmtUSD } from "@/lib/api";
import { playCelebration, hapticCelebration, playInitialization, hapticInitialization } from "@/lib/celebrationAudio";
import { Coins, X, FlaskConical } from "lucide-react";

// Profit Celebration: fires ONLY on backend-confirmed realized-value events
// (realized gains, wildcard harvests, income, reconciled treasury highs).
// The frontend never infers celebration from NAV movement.

const CelebrationContext = createContext(null);
export const useCelebration = () => useContext(CelebrationContext);

const INTENSITY_CLASS = { MICRO: "celeb-micro", STANDARD: "celeb-standard", MAJOR: "celeb-major", MILESTONE: "celeb-milestone" };

export function CelebrationProvider({ children }) {
  const [prefs, setPrefs] = useState(null);
  const [active, setActive] = useState(null);
  const queueRef = useRef([]);
  const seenRef = useRef(new Set());
  const sinceRef = useRef(new Date().toISOString());
  const busyRef = useRef(false);

  const reloadPrefs = useCallback(() => {
    api.get("/celebrations/settings").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);
  useEffect(reloadPrefs, [reloadPrefs]);

  const fire = useCallback((ev, overrideMode = null) => {
    const mode = overrideMode || prefs?.mode || "HUSSAR";
    if (mode === "OFF") return;
    const effective = overrideMode ? { mode, sound: true, haptics: true, visual: true } : { mode, ...prefs };
    if (ev.type === "TREASURY_INITIALIZED") {
      if (effective.sound) playInitialization();
      if (effective.haptics) hapticInitialization();
    } else {
      if (effective.sound) playCelebration(mode, ev.intensity);
      if (effective.haptics) hapticCelebration(mode);
    }
    if (effective.visual !== false) setActive({ ...ev, mode });
  }, [prefs]);

  const pump = useCallback(() => {
    if (busyRef.current || !queueRef.current.length) return;
    busyRef.current = true;
    fire(queueRef.current.shift());
  }, [fire]);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      setActive(null);
      busyRef.current = false;
      setTimeout(pump, 700);
    }, 4200);
    return () => clearTimeout(t);
  }, [active, pump]);

  useEffect(() => {
    if (!prefs || prefs.mode === "OFF") return;
    if (!prefs.sound && !prefs.haptics && !prefs.visual) return;
    let mounted = true;
    const poll = async () => {
      try {
        const [r, f] = await Promise.all([
          api.get("/celebrations/events", { params: { since: sinceRef.current } }),
          api.get("/celebrations/family-events", { params: { since: sinceRef.current } }).catch(() => ({ data: { events: [] } })),
        ]);
        if (!mounted) return;
        sinceRef.current = r.data.server_time || sinceRef.current;
        const fresh = [...(r.data.events || []), ...(f.data.events || [])]
          .filter((e) => !seenRef.current.has(e.id));
        fresh.forEach((e) => { seenRef.current.add(e.id); queueRef.current.push(e); });
        pump();
      } catch { /* poll again next cycle */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { mounted = false; clearInterval(t); };
  }, [prefs, pump]);

  const preview = useCallback((mode, intensity = "STANDARD") => {
    fire({
      id: `test-${Date.now()}`, type: "TEST_PREVIEW", message: "TEST CELEBRATION",
      account_name: "Preview", source: "PREVIEW", asset: "BTC",
      gross: 82.40, tax_reserved: 27.60, net: 54.80, intensity, test: true,
      detail: "Preview only — no ledger event or financial record was created.",
    }, mode);
  }, [fire]);

  return (
    <CelebrationContext.Provider value={{ prefs, reloadPrefs, preview }}>
      {children}
      {active && prefs?.visual !== false && (
        <CelebrationOverlay ev={active} onClose={() => { setActive(null); busyRef.current = false; setTimeout(pump, 400); }} />
      )}
    </CelebrationContext.Provider>
  );
}

function CelebrationOverlay({ ev, onClose }) {
  const scale = INTENSITY_CLASS[ev.intensity] || "celeb-standard";
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center pointer-events-none" data-testid="celebration-overlay">
      <div className={`celebrate-in relative panel px-8 py-7 text-center border-[#D4AF37]/60 pointer-events-auto ${scale}`}
        style={{ boxShadow: "0 0 60px rgba(212,175,55,0.18), inset 0 0 40px rgba(212,175,55,0.05)" }}>
        <div className="gold-pulse-ring" />
        <button onClick={onClose} data-testid="celebration-dismiss"
          className="absolute top-2 right-2 text-slate-500 hover:text-slate-200 transition-colors">
          <X className="w-4 h-4" />
        </button>
        <Coins className="w-8 h-8 gold-text mx-auto mb-3 coin-shimmer" />
        <div className="text-[11px] tracking-[0.3em] uppercase text-slate-400 font-semibold" data-testid="celebration-message">
          {ev.message}
        </div>
        {ev.test && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-400/90 uppercase tracking-widest" data-testid="celebration-test-badge">
            <FlaskConical className="w-3 h-3" /> Test preview
          </div>
        )}
        <div className="font-mono text-3xl font-bold gold-text mt-3 value-count" data-testid="celebration-amount">
          {ev.type === "TREASURY_INITIALIZED" ? fmtUSD(ev.net) : `+${fmtUSD(ev.net)}`}
        </div>
        <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-0.5">
          {ev.type === "TREASURY_INITIALIZED" ? "founding capital recorded" : "net value created"}
        </div>
        {ev.detail && <div className="text-[11px] font-mono text-slate-400 mt-3" data-testid="celebration-detail">{ev.detail}</div>}
        <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 border border-[#2D323E] rounded-sm px-2.5 py-1" data-testid="celebration-account">
          {ev.account_name}{ev.asset ? ` · ${ev.asset}` : ""}{ev.source && ev.source !== "PREVIEW" ? ` · ${String(ev.source).replace(/_/g, " ")}` : ""}
        </div>
      </div>
    </div>
  );
}
